import { useEffect, useState } from "react";
import api from "@/lib/api";

/** Icônes officielles Facebook & Instagram (SVG vectoriel, style ligne fine "prestige") */
export function FacebookIcon({ className = "w-4 h-4" }) {
  return (
    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" className={className} fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
    </svg>
  );
}

export function InstagramIcon({ className = "w-4 h-4" }) {
  return (
    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" className={className} fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="3" width="18" height="18" rx="5" ry="5" />
      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
      <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
    </svg>
  );
}

/**
 * Hook léger — retourne les URLs de FB / IG stockées côté /api/site.
 * Fallback : "/" si l'admin ne les a pas encore renseignées.
 */
export function useSocialLinks() {
  const [links, setLinks] = useState({ facebook: "/", instagram: "/", raw: {} });
  useEffect(() => {
    api.get("/site").then((r) => {
      const d = r.data || {};
      setLinks({
        facebook: d.facebook && d.facebook.trim() ? d.facebook : "/",
        instagram: d.instagram && d.instagram.trim() ? d.instagram : "/",
        raw: { fb: d.facebook || "", ig: d.instagram || "" },
      });
    }).catch(() => {});
  }, []);
  return links;
}

/**
 * Barre de liens sociaux — style "prestige".
 * `variant` : "nav" (petit, discret) · "footer" (moyen) · "cta" (bouton "Nous suivre")
 */
export default function SocialLinks({ variant = "nav", className = "" }) {
  const { facebook, instagram, raw } = useSocialLinks();
  const fbActive = raw.fb && raw.fb.trim();
  const igActive = raw.ig && raw.ig.trim();

  if (variant === "cta") {
    return (
      <div className={`bg-[#1E1E1E] border border-white/10 rounded-md p-5 hover:border-[#C8A75A]/40 transition-colors ${className}`}>
        <p className="text-[10px] uppercase tracking-[0.25em] text-white/50 mb-3">Nous suivre</p>
        <div className="flex items-center gap-3">
          <SocialLink href={facebook} active={fbActive} label="Facebook" testid="social-cta-fb">
            <FacebookIcon className="w-5 h-5" />
            <span className="uppercase tracking-widest text-[11px] font-medium">Facebook</span>
          </SocialLink>
          <SocialLink href={instagram} active={igActive} label="Instagram" testid="social-cta-ig">
            <InstagramIcon className="w-5 h-5" />
            <span className="uppercase tracking-widest text-[11px] font-medium">Instagram</span>
          </SocialLink>
        </div>
        {(!fbActive && !igActive) && (
          <p className="text-[10px] text-white/30 mt-3 italic">Liens à venir</p>
        )}
      </div>
    );
  }

  const size = variant === "footer" ? "w-5 h-5" : "w-[18px] h-[18px]";
  const box = variant === "footer" ? "w-10 h-10" : "w-9 h-9";

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <IconBtn href={facebook} active={fbActive} label="Facebook" size={box} testid={`social-${variant}-fb`}>
        <FacebookIcon className={size} />
      </IconBtn>
      <IconBtn href={instagram} active={igActive} label="Instagram" size={box} testid={`social-${variant}-ig`}>
        <InstagramIcon className={size} />
      </IconBtn>
    </div>
  );
}

function IconBtn({ href, active, label, size, testid, children }) {
  const external = active && /^https?:\/\//i.test(href);
  return (
    <a
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
      aria-label={`${label}${active ? "" : " (bientôt disponible)"}`}
      title={`${label}${active ? "" : " · Bientôt disponible"}`}
      data-testid={testid}
      className={`${size} rounded-full flex items-center justify-center border border-[#C8A75A]/25 text-[#C8A75A]/80 hover:text-[#C8A75A] hover:border-[#C8A75A]/70 hover:bg-[#C8A75A]/10 transition-all duration-300 ${active ? "" : "opacity-60"}`}
    >
      {children}
    </a>
  );
}

function SocialLink({ href, active, label, testid, children }) {
  const external = active && /^https?:\/\//i.test(href);
  return (
    <a
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
      aria-label={`${label}${active ? "" : " (bientôt disponible)"}`}
      title={`${label}${active ? "" : " · Bientôt disponible"}`}
      data-testid={testid}
      className={`inline-flex items-center gap-2 px-4 py-2 rounded-md border border-[#C8A75A]/40 text-[#C8A75A]/90 hover:text-black hover:bg-[#C8A75A] transition-all duration-300 ${active ? "" : "opacity-70"}`}
    >
      {children}
    </a>
  );
}
